import React, { createContext, useContext, useEffect, useState } from 'react';
import { exchangeBetterAuthSession } from '@/lib/api';
import { sessionBootstrapStep } from '@/lib/session-bootstrap';
import { User } from '@/types/User';
import { clearAuthMarkers } from '@/lib/auth-prefs';
import { useAuthStore } from '@/stores/authStore';
import { onAuthEvent, otherTabTeamAfter, signsOutThisTab } from '@/lib/auth-channel';
import { redirectToLoginForExpiredSession, willRedirectToLogin } from '@/lib/auth-redirect';
import { rememberTeamChoice } from '@/lib/tab-team';
import { beginTeamExit } from '@/utils/team-exit';
import { clearPrefsOnSignOut } from '@/lib/ui-prefs';

interface AuthResponse {
  token?: string;
  user?: {
    id: string;
    email: string;
    displayname?: string;
    photourl?: string;
    role: string;
    team_displayname?: string;
    teams?: {
      id: string;
      displayname: string;
      shopify_store_domain: string;
    }[];
    active_team?: {
      id: string;
      displayname: string;
      shopify_store_domain: string;
    };
  };
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: () => Promise<void>;
  logout: (loginPath?: string) => Promise<void>;
  setUser: (user: User | null) => void;
  /** jflo didn't answer the session check at load; this tab's stored user, if any, is kept. */
  sessionUnreachable: boolean;
  /** Ask for the session again (the "Can't reach jflo" Retry). */
  retrySession: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const {
    user,
    loading,
    error,
    login,
    logout,
    setUser,
    setLoading,
    updateUserAndToken,
    sessionUnreachable,
    setSessionUnreachable,
  } = useAuthStore();
  // Each Retry asks again; a run that StrictMode or a newer attempt replaced is dropped.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      const tokenAtStart = useAuthStore.getState().user?.token ?? null;
      const sessionRes = await exchangeBetterAuthSession();
      if (cancelled) return;
      const step = sessionBootstrapStep(
        sessionRes,
        tokenAtStart,
        useAuthStore.getState().user?.token ?? null
      );
      if (step === 'apply') {
        updateUserAndToken(sessionRes.data as AuthResponse);
      } else if (step === 'signed-out') {
        setUser(null);
      } else if (step === 'unreachable') {
        // No answer, or a server error: not signed out (decision 11 a). Keep this tab's stored user.
        // /auth/refresh needs the same cookie, so it can't succeed where /auth/session failed.
        setSessionUnreachable(true);
      }
      // 'superseded': the tab signed in, out or renewed while this was out; what it holds now stands.
      clearAuthMarkers();
      setLoading(false);
    };

    void bootstrap();

    return () => {
      cancelled = true;
    };
  }, [attempt, setUser, setLoading, setSessionUnreachable, updateUserAndToken]);

  const retrySession = () => {
    setSessionUnreachable(false);
    setLoading(true);
    setAttempt((n) => n + 1);
  };

  // Another tab switched team: offer to follow, rather than following (decision 8). Another tab
  // signed out: so does this one (decision 7 a). The sign-out itself already happened there.
  useEffect(
    () =>
      onAuthEvent((event) => {
        const state = useAuthStore.getState();
        if (event.type === 'signed-out') {
          if (!signsOutThisTab(event, { userId: state.user?.id })) {
            // Already signed out here (an expired session): nothing to leave, but this tab's
            // sessionStorage may still hold drafts (decision 31 a).
            clearPrefsOnSignOut();
            return;
          }
          void (async () => {
            // Unmount the team pages first, so their guards don't ask and their live connection
            // closes. Only when the page really leaves: it silences the guards until reload.
            if (willRedirectToLogin()) await beginTeamExit('Signing out…');
            // That tab cleared the shared localStorage; this clears this tab's sessionStorage, and
            // anything this tab wrote since (decision 31 a).
            clearPrefsOnSignOut();
            redirectToLoginForExpiredSession();
          })();
          return;
        }
        state.setOtherTabTeam(
          otherTabTeamAfter(
            event,
            { userId: state.user?.id, teamId: state.user?.active_team?.id },
            state.otherTabTeam
          )
        );
      }),
    []
  );

  // A brand-new tab (Cmd-click, a typed URL) opens on the team of the tab used last.
  const tabTeamId = user?.active_team?.id;
  useEffect(() => {
    if (!tabTeamId) return;
    const remember = () => {
      if (document.visibilityState === 'visible') rememberTeamChoice(tabTeamId);
    };
    remember();
    window.addEventListener('focus', remember);
    document.addEventListener('visibilitychange', remember);
    return () => {
      window.removeEventListener('focus', remember);
      document.removeEventListener('visibilitychange', remember);
    };
  }, [tabTeamId]);

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    setUser,
    sessionUnreachable,
    retrySession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

if (typeof window !== 'undefined') {
  const checkAuthComplete = () => {
    const store = useAuthStore.getState();
    if (store.user && store.authAttempted) {
      clearAuthMarkers();
      store.setAuthAttempted(false);
    }
  };

  checkAuthComplete();
  const interval = setInterval(checkAuthComplete, 1000);
  setTimeout(() => clearInterval(interval), 10000);
}
